/**
 * Presence Hook
 *
 * Wraps Appwrite's Presences API to provide two things for a given "page"
 * (a record being viewed/edited, e.g. `quote:123`):
 *
 * - activeUsers: everyone currently present on the page (for a stacked
 *   avatar indicator), each optionally flagged with the field they're editing
 * - trackFieldEdit / clearFieldEdit / getFieldEditor / isCurrentUserEditing:
 *   broadcast and read field-level "who's editing this input" state
 *
 * Liveness rule — important: a peer is considered present because WE heard
 * from them recently, measured on OUR clock. We never compare a peer's
 * `expiresAt` (which their browser computed from THEIR clock) against our own
 * Date.now(); two machines whose clocks differ by more than the TTL would then
 * each classify the other as permanently expired, and presence would stay dark
 * until an NTP resync. `expiresAt` is still sent so the server can reap
 * abandoned records, but it is never used for a local visibility decision.
 *
 * Every layer self-heals: the heartbeat re-publishes us (recovering from a
 * failed write or an expired session once it is renewed), and the periodic
 * resync re-lists everyone (recovering from realtime events missed while the
 * socket was down).
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { presences, realtime, getCurrentUser, ID, Query, Permission, Role, Channel } from '../services/appwrite';

const HEARTBEAT_MS = 20000;
// Server-side record lifetime. Comfortably longer than the heartbeat so a
// single slow/failed write never makes us vanish for other people.
const PRESENCE_TTL_MS = 75000;
// Drop a peer after ~3 missed heartbeats, measured from when WE last saw them.
const PEER_TIMEOUT_MS = 65000;
const SWEEP_MS = 5000;
// Full re-list, so a subscription gap (socket drop, backoff, missed event)
// repairs itself instead of leaving the roster permanently wrong.
const RESYNC_MS = 30000;

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

// Appwrite returns metadata as an object, but tolerate a JSON string the way
// the rest of this codebase does for stringified columns.
function readMetadata(presence) {
  const meta = presence?.metadata;
  if (!meta) return {};
  if (typeof meta === 'string') {
    try { return JSON.parse(meta); } catch { return {}; }
  }
  return meta;
}

/**
 * @param {string|null} pageKey - unique key identifying the record/page being
 *   collaborated on (e.g. `quote:${quoteId}`). Pass null/undefined to disable
 *   (e.g. while a record is still being created and has no id yet).
 */
export function usePresence(pageKey) {
  const [currentUser, setCurrentUser] = useState(null);
  const [presenceError, setPresenceError] = useState(null);
  const [, forceUpdate] = useState(0);
  const rerender = useCallback(() => forceUpdate(n => n + 1), []);

  const presenceIdRef = useRef(null);
  const editingFieldRef = useRef(null);
  // presenceId -> { presence, lastSeen } where lastSeen is OUR Date.now()
  const peersRef = useRef(new Map());
  const failureCountRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    getCurrentUser().then(user => {
      if (!cancelled) setCurrentUser(user);
    });
    return () => { cancelled = true; };
  }, []);

  // Record a peer against our own clock, so liveness never depends on their clock.
  const notePeer = useCallback((presence) => {
    peersRef.current.set(presence.$id, { presence, lastSeen: Date.now() });
  }, []);

  const publish = useCallback(async (status) => {
    if (!currentUser?.id || !currentUser?.teamId || !pageKey) return;
    if (!presenceIdRef.current) presenceIdRef.current = ID.unique();

    try {
      await presences.upsert({
        presenceId: presenceIdRef.current,
        status,
        metadata: {
          page: pageKey,
          userName: currentUser.name || currentUser.email || 'Unknown user',
          editingField: editingFieldRef.current || null
        },
        expiresAt: new Date(Date.now() + PRESENCE_TTL_MS).toISOString(),
        permissions: [
          Permission.read(Role.team(currentUser.teamId)),
          Permission.update(Role.user(currentUser.id)),
          Permission.delete(Role.user(currentUser.id))
        ]
      });
      failureCountRef.current = 0;
      setPresenceError(prev => (prev ? null : prev));
    } catch (err) {
      // Don't fail silently — a dead session or a permissions change would
      // otherwise just look like "presence stopped working" with no signal.
      failureCountRef.current += 1;
      console.error(
        `[Presence] upsert failed (${failureCountRef.current}x):`,
        err.code || '', err.type || '', err.message
      );
      if (failureCountRef.current >= 3) {
        setPresenceError(err);
      }
    }
  }, [currentUser, pageKey]);

  // Join the page and keep the presence record alive with a heartbeat
  useEffect(() => {
    if (!currentUser?.id || !pageKey) return;

    publish('online');
    const heartbeat = setInterval(() => publish('online'), HEARTBEAT_MS);

    return () => {
      clearInterval(heartbeat);
      const id = presenceIdRef.current;
      presenceIdRef.current = null;
      editingFieldRef.current = null;
      if (id) {
        presences.delete({ presenceId: id }).catch(() => {});
      }
    };
  }, [currentUser?.id, pageKey, publish]);

  // Listen for other users' presence changes on this page
  useEffect(() => {
    if (!pageKey) return;

    const peers = peersRef.current;
    let closed = false;
    let subscription = null;

    realtime.subscribe(Channel.presences(), (response) => {
      const payload = response.payload;
      if (!payload) return;

      // Deletes are processed by id alone. The delete payload is not guaranteed
      // to carry metadata, so gating on metadata.page here would strand the
      // record in the roster forever as a ghost.
      if (response.events?.some(e => e.endsWith('.delete'))) {
        if (peers.delete(payload.$id)) rerender();
        return;
      }

      if (readMetadata(payload).page !== pageKey) return;
      notePeer(payload);
      rerender();
    }).then(sub => {
      subscription = sub;
      // Unsubscribing before the promise settled — close it now.
      if (closed) sub.unsubscribe?.();
    }).catch(err => {
      console.error('[Presence] realtime subscribe failed:', err.message);
    });

    return () => {
      closed = true;
      subscription?.unsubscribe?.();
      peers.clear();
    };
  }, [pageKey, rerender, notePeer]);

  // Snapshot on join + periodic resync, plus a sweep that drops peers we have
  // not heard from recently (closed laptop, lost connection, missed event).
  useEffect(() => {
    if (!currentUser?.id || !pageKey) return;

    let cancelled = false;

    const resync = async () => {
      try {
        // The server already filters out expired entries for us.
        const result = await presences.list({ queries: [Query.limit(100)] });
        if (cancelled) return;

        const seen = new Set();
        (result.presences || []).forEach(p => {
          if (readMetadata(p).page !== pageKey) return;
          seen.add(p.$id);
          notePeer(p);
        });

        // Anything the server no longer reports is gone, regardless of what our
        // local timers think.
        peersRef.current.forEach((_, id) => {
          if (!seen.has(id)) peersRef.current.delete(id);
        });
        rerender();
      } catch (err) {
        console.error('[Presence] list failed:', err.code || '', err.message);
      }
    };

    resync();
    const resyncTimer = setInterval(resync, RESYNC_MS);

    const sweep = setInterval(() => {
      const cutoff = Date.now() - PEER_TIMEOUT_MS;
      let changed = false;
      peersRef.current.forEach((entry, id) => {
        if (entry.lastSeen < cutoff) {
          peersRef.current.delete(id);
          changed = true;
        }
      });
      if (changed) rerender();
    }, SWEEP_MS);

    return () => {
      cancelled = true;
      clearInterval(resyncTimer);
      clearInterval(sweep);
    };
  }, [currentUser?.id, pageKey, rerender, notePeer]);

  const trackFieldEdit = useCallback((fieldName) => {
    editingFieldRef.current = fieldName;
    publish('online');
  }, [publish]);

  const clearFieldEdit = useCallback(() => {
    editingFieldRef.current = null;
    publish('online');
  }, [publish]);

  const isCurrentUserEditing = useCallback((fieldName) => {
    return editingFieldRef.current === fieldName;
  }, []);

  // One entry per user (latest presence wins if they have several, e.g. two tabs)
  const byUser = new Map();
  peersRef.current.forEach(({ presence }) => {
    const existing = byUser.get(presence.userId);
    if (!existing || new Date(presence.$updatedAt) >= new Date(existing.$updatedAt)) {
      byUser.set(presence.userId, presence);
    }
  });

  const activeUsers = Array.from(byUser.values()).map(p => {
    const meta = readMetadata(p);
    const userName = meta.userName || 'Unknown user';
    return {
      user_id: p.userId,
      id: p.userId,
      user_name: userName,
      userName,
      user_initials: getInitials(userName),
      editing_field: meta.editingField || null,
      isSelf: p.userId === currentUser?.id
    };
  });

  // Recreated each render (activeUsers is derived fresh above, not memoized) -
  // cheap, and callers use it synchronously rather than stashing the reference.
  const getFieldEditor = (fieldName) => {
    return activeUsers.find(u => !u.isSelf && u.editing_field === fieldName) || null;
  };

  return {
    activeUsers,
    currentUser,
    presenceError,
    trackFieldEdit,
    clearFieldEdit,
    getFieldEditor,
    isCurrentUserEditing
  };
}

export default usePresence;
